import { useState, useCallback } from 'react'
import { Globe, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { WebCrawlProgress } from '@/components/sources/WebCrawlProgress'
import { webSourcesApi, type CrawlConfig } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'

interface WebUrlTabProps {
  disabled: boolean
  onComplete: (sourceId: string | null) => void
}

export function WebUrlTab({ disabled, onComplete }: WebUrlTabProps) {
  const { accountId, projectId } = useSessionStore()
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [crawlEnabled, setCrawlEnabled] = useState(false)
  const [maxDepth, setMaxDepth] = useState(2)
  const [maxPages, setMaxPages] = useState(20)
  const [isLoading, setIsLoading] = useState(false)
  const [webSourceId, setWebSourceId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const validateUrl = (value: string): boolean => {
    try {
      const parsed = new URL(value)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  const handleSubmit = async () => {
    if (!accountId || !projectId) return

    if (!validateUrl(url)) {
      setUrlError('Enter a valid URL (https://...)')
      return
    }

    setUrlError(null)
    setError(null)
    setIsLoading(true)

    try {
      const crawlConfig: CrawlConfig | undefined = crawlEnabled
        ? { maxDepth, maxPages }
        : undefined

      const result = await webSourcesApi.scrape(accountId, projectId, { url, crawlConfig })
      if (result.webSourceId) {
        setWebSourceId(result.webSourceId)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scrape')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCrawlComplete = useCallback(
    (sourceId: string | null) => {
      onComplete(sourceId)
    },
    [onComplete]
  )

  // Show progress when crawling
  if (webSourceId && accountId && projectId) {
    return (
      <div className="space-y-3">
        <WebCrawlProgress
          webSourceId={webSourceId}
          accountId={accountId}
          projectId={projectId}
          onComplete={handleCrawlComplete}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Website URL</Label>
        <div className="relative mt-1">
          <Globe className="absolute left-2.5 top-2 w-4 h-4 text-text-muted" />
          <Input
            placeholder="https://competitor.com"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
              setUrlError(null)
            }}
            className="pl-8 h-8 text-sm"
            disabled={disabled || isLoading}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
        </div>
        {urlError && (
          <p className="text-xs text-red-500 mt-1">{urlError}</p>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={crawlEnabled}
          onChange={(e) => setCrawlEnabled(e.target.checked)}
          disabled={disabled || isLoading}
          className="rounded"
        />
        <span>Crawl linked pages</span>
        {crawlEnabled && (
          <span className="text-xs text-text-muted ml-auto">
            Depth: {maxDepth} &middot; Max: {maxPages} pages
          </span>
        )}
      </label>

      {crawlEnabled && (
        <div className="flex gap-3 pl-6">
          <div className="flex-1">
            <Label className="text-xs">Depth</Label>
            <select
              value={maxDepth}
              onChange={(e) => setMaxDepth(Number(e.target.value))}
              className="w-full mt-1 h-8 px-2 text-sm border border-warm-300 rounded-md bg-white"
              disabled={disabled || isLoading}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div className="flex-1">
            <Label className="text-xs">Max Pages</Label>
            <select
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              className="w-full mt-1 h-8 px-2 text-sm border border-warm-300 rounded-md bg-white"
              disabled={disabled || isLoading}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}

      <Button
        onClick={handleSubmit}
        disabled={disabled || isLoading || !url.trim()}
        className="w-full"
        size="sm"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <Globe className="w-4 h-4 mr-2" />
            Start Scraping
          </>
        )}
      </Button>
    </div>
  )
}
